import React, { type MouseEvent, type ReactNode, useCallback, useMemo } from "react";

import { Form } from "react-bootstrap";

import classNames from "classnames";

import type { ResaleStatusComponentType } from "./ProvisionalResaleTable";

export interface TableHeader<T> {
  label: string;
  accessor: keyof T;
  componentType?: ResaleStatusComponentType;
  className?: string;
}

interface TableContentsProps<T> {
  headers: TableHeader<T>[];
  data: T[];
  keyAccessor: keyof T;
  customStyles?: { [key: string]: string };
  mapColumnValue?: (value: T[keyof T], key: keyof T, row?: T) => { component: ReactNode };
  onRowClick?: (row: T) => void;
  isRowDisabled?: (row: T) => boolean;
  isRowChecked?: (row: T) => boolean;
  isRowCheckBoxDisabled?: (row: T) => boolean;
  showCheckBox?: boolean;
  handleCheckBoxChange?: (row: T) => void;
}

interface TableRowProps<T> extends Omit<TableContentsProps<T>, "data"> {
  row: T;
}

const defaultCustomStyles: { [key: string]: string } = {};

const TableRow = <T,>({
  headers,
  row,
  keyAccessor,
  mapColumnValue,
  customStyles = defaultCustomStyles,
  onRowClick,
  isRowDisabled,
  isRowChecked,
  isRowCheckBoxDisabled,
  showCheckBox,
  handleCheckBoxChange,
}: TableRowProps<T>) => {
  const lookupColumnValue = useCallback(
    (row: T, accessor: keyof T, keyAccessor: keyof T) => {
      const cellValue = row[accessor];

      let componentToRender: ReactNode = undefined;

      if (mapColumnValue) {
        const { component } = mapColumnValue(cellValue, accessor, row);

        componentToRender = component;
      }

      return <React.Fragment key={`${row[keyAccessor]}-${String(accessor)}`}>{componentToRender}</React.Fragment>;
    },
    [mapColumnValue],
  );

  const disabled = isRowDisabled?.(row);
  const rowCheckBoxDisabled = useMemo(() => {
    return isRowCheckBoxDisabled ? isRowCheckBoxDisabled(row) : false;
  }, [isRowCheckBoxDisabled, row]);
  const checked = useMemo(() => {
    return isRowChecked ? isRowChecked(row) : false;
  }, [isRowChecked, row]);

  const handleOnRowClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      onRowClick?.(row);
    },
    [onRowClick, row],
  );

  return (
    <tr
      key={String(row[keyAccessor])}
      className={classNames(
        {
          [customStyles["disabled-row"]]: disabled,
        },
        customStyles["bottom-border"],
      )}
      onClick={(event) => handleOnRowClick(event)}
    >
      {showCheckBox && (
        <td className="align-content-center">
          <Form.Check
            type="checkbox"
            checked={checked ?? false}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleCheckBoxChange?.(row)}
            disabled={rowCheckBoxDisabled}
            className={classNames({
              [customStyles["custom-disabled-checkbox"]]: rowCheckBoxDisabled,
            })}
          />
        </td>
      )}
      {headers.map(({ accessor }) => lookupColumnValue(row, accessor, keyAccessor))}
    </tr>
  );
};

export const TableContents = <T,>({
  headers,
  data,
  keyAccessor,
  customStyles,
  mapColumnValue,
  onRowClick,
  isRowDisabled,
  isRowChecked,
  isRowCheckBoxDisabled,
  handleCheckBoxChange,
  showCheckBox = false,
}: TableContentsProps<T>) => {
  return (
    <tbody>
      {data.map((row) => {
        return (
          <TableRow
            key={String(row[keyAccessor])}
            headers={headers}
            row={row}
            keyAccessor={keyAccessor}
            customStyles={customStyles}
            mapColumnValue={mapColumnValue}
            onRowClick={onRowClick}
            isRowDisabled={isRowDisabled}
            isRowChecked={isRowChecked}
            isRowCheckBoxDisabled={isRowCheckBoxDisabled}
            showCheckBox={showCheckBox}
            handleCheckBoxChange={handleCheckBoxChange}
          />
        );
      })}
    </tbody>
  );
};

type ColumnFormatter = (arg0: string) => string;

export type Header<T> = {
  label: string;
  accessor: keyof T;
  formatter?: ColumnFormatter;
};

interface SimpleTableContentsProps<T> {
  headers: Header<T>[];
  data: T[];
  keyAccessor: keyof T;
}

export const SimpleTableContents = <T extends object>({ headers, data, keyAccessor }: SimpleTableContentsProps<T>) => {
  const lookupColumnValue = (row: T, key: keyof T, formatter?: ColumnFormatter) => {
    const value = String(row[key]);
    return <td key={`${row[keyAccessor]}-${String(row[key])}`}>{formatter ? formatter(value) : value}</td>;
  };

  const toRow = (row: T) => (
    <tr key={String(row[keyAccessor])}>
      {headers.map(({ accessor, formatter }) => lookupColumnValue(row, accessor, formatter))}
    </tr>
  );

  return <tbody>{data.map(toRow)}</tbody>;
};
